import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight, Check, FileText, Film, Sparkles, UploadCloud, WandSparkles } from 'lucide-react';
import GenerationOverlay from '../components/GenerationOverlay';
import {
  cloneDemoShots,
  DEMO_PROJECT_TITLE,
  DEMO_SCRIPT,
  demoDuration,
  parseScriptLocally,
} from '../demo/mockProject';

const workflowSteps = ['剧本导入', '智能解析', '分镜定稿', '分镜图', '视频 Snap', '完整成片'];

function VideoPage({ onNavigate, demoMode }) {
  const [script, setScript] = useState(demoMode ? DEMO_SCRIPT : '');
  const [fileName, setFileName] = useState(demoMode ? 'Midnight_Circuit.fountain' : '');
  const [projectTitle, setProjectTitle] = useState(demoMode ? DEMO_PROJECT_TITLE : '未命名项目');
  const [isParsing, setIsParsing] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef(null);

  useEffect(() => {
    if (demoMode && !script.trim()) {
      setScript(DEMO_SCRIPT);
      setFileName('Midnight_Circuit.fountain');
      setProjectTitle(DEMO_PROJECT_TITLE);
    }
  }, [demoMode, script]);

  const handleFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setScript(String(reader.result || ''));
      setFileName(file.name);
      setProjectTitle(file.name.replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' '));
      setError('');
    };
    reader.onerror = () => setError('文件读取失败，请重试或直接粘贴剧本。');
    reader.readAsText(file);
  };

  const handleParse = () => {
    if (!script.trim()) {
      setError('请先上传或粘贴英文剧本。');
      return;
    }
    setError('');
    setIsParsing(true);
  };

  const finishParsing = () => {
    const shots = demoMode ? cloneDemoShots() : parseScriptLocally(script);
    setIsParsing(false);
    onNavigate('storyboard-result', {
      prompt: script,
      shots,
      workflow: 'video',
      projectTitle,
      fileName,
      demoMode,
    });
  };

  return (
    <div className="min-h-full bg-[#0d0f13] p-6 md:p-8">
      <div className="mx-auto max-w-7xl">
        <div className="mb-8 flex flex-col justify-between gap-4 lg:flex-row lg:items-end">
          <div>
            <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.22em] text-blue-400">
              <WandSparkles className="h-4 w-4" /> AI Video Studio
            </div>
            <h1 className="text-3xl font-semibold tracking-tight text-white md:text-4xl">从英文剧本到完整成片</h1>
            <p className="mt-3 max-w-2xl text-sm leading-6 text-gray-400">上传完整剧本，自动识别场景、人物、动作与情绪，并生成可编辑的专业镜头方案。</p>
          </div>
          <div className="flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs text-gray-400">
            <span className={`h-2 w-2 rounded-full ${demoMode ? 'bg-emerald-400' : 'bg-blue-400'}`} />
            {demoMode ? '演示素材已就绪' : '创世 First 1.0 Beta'}
          </div>
        </div>

        <div className="mb-8 overflow-x-auto rounded-2xl border border-white/10 bg-[#15171c] p-4">
          <div className="flex min-w-[760px] items-center">
            {workflowSteps.map((step, index) => (
              <React.Fragment key={step}>
                <div className="flex items-center gap-2">
                  <span className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold ${index === 0 ? 'bg-blue-500 text-white' : 'bg-white/5 text-gray-500'}`}>
                    {index === 0 ? <Check className="h-4 w-4" /> : index + 1}
                  </span>
                  <span className={index === 0 ? 'text-sm text-white' : 'text-sm text-gray-500'}>{step}</span>
                </div>
                {index < workflowSteps.length - 1 && <div className="mx-4 h-px flex-1 bg-white/10" />}
              </React.Fragment>
            ))}
          </div>
        </div>

        {demoMode && (
          <button onClick={() => onNavigate('demo-showcase')} className="group mb-6 flex w-full flex-col justify-between gap-4 rounded-2xl border border-emerald-400/20 bg-emerald-400/[0.06] p-5 text-left transition hover:bg-emerald-400/[0.1] md:flex-row md:items-center">
            <span>
              <span className="flex items-center gap-2 text-sm font-semibold text-white"><Film className="h-5 w-5 text-emerald-400" /> 跳过手动步骤，一键生成完整演示</span>
              <span className="mt-1.5 block text-xs text-gray-400">直接展示 14 张机车分镜、真实视频 Snap 和完整成片</span>
            </span>
            <span className="flex items-center gap-2 text-sm font-semibold text-emerald-300">立即生成 <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" /></span>
          </button>
        )}

        <div className="grid gap-6 lg:grid-cols-[1.3fr_0.7fr]">
          <section className="rounded-3xl border border-white/10 bg-[#15171c] p-6 md:p-8">
            <div className="mb-5 flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gray-500">Step 01</p>
                <h2 className="mt-2 text-xl font-semibold text-white">导入剧本</h2>
              </div>
              <span className="rounded-lg bg-blue-500/10 px-3 py-1 text-xs text-blue-400">支持英文长剧本</span>
            </div>

            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              onDragOver={(event) => event.preventDefault()}
              onDrop={(event) => {
                event.preventDefault();
                handleFile(event.dataTransfer.files?.[0]);
              }}
              className="group mb-5 flex w-full items-center justify-between rounded-2xl border border-dashed border-white/15 bg-white/[0.025] p-4 text-left transition hover:border-blue-500/50 hover:bg-blue-500/5"
            >
              <span className="flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/5 text-gray-400 group-hover:text-blue-400"><UploadCloud className="h-5 w-5" /></span>
                <span>
                  <span className="block text-sm font-medium text-white">{fileName || '上传 .txt / .md / .fountain 文件'}</span>
                  <span className="mt-1 block text-xs text-gray-500">拖放文件到这里，或点击选择</span>
                </span>
              </span>
              <span className="text-xs text-blue-400">选择文件</span>
            </button>
            <input ref={fileRef} type="file" accept=".txt,.md,.fountain,text/plain" className="hidden" onChange={(event) => handleFile(event.target.files?.[0])} />

            <label className="mb-2 block text-xs font-medium text-gray-400">剧本内容</label>
            <textarea
              value={script}
              onChange={(event) => { setScript(event.target.value); setError(''); }}
              placeholder="Paste your full English screenplay here…"
              className="h-[360px] w-full resize-none rounded-2xl border border-white/10 bg-[#0e1014] p-5 font-mono text-sm leading-6 text-gray-300 outline-none transition placeholder:text-gray-700 focus:border-blue-500/60"
            />
            {error && <p className="mt-3 text-sm text-red-400">{error}</p>}

            <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-xs text-gray-500">AI 将解析台词、场景、人物动作与情绪描述</p>
              <button onClick={handleParse} className="flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-blue-500">
                <Sparkles className="h-4 w-4" /> 智能解析剧本 <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </section>

          <aside className="space-y-5">
            <div className="overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-[#202947] to-[#171921] p-6">
              <Film className="h-8 w-8 text-blue-400" />
              <h3 className="mt-5 text-lg font-semibold text-white">专业镜头库</h3>
              <p className="mt-2 text-sm leading-6 text-gray-400">自动为每个镜头匹配景别、运镜、光影、人物神态、构图和道具环境，所有参数都可逐帧修改。</p>
              <div className="mt-5 flex flex-wrap gap-2">
                {['极特写', '中景', '大全景', '环绕', '跟拍', '低机位'].map((item) => <span key={item} className="rounded-full border border-white/10 bg-black/20 px-3 py-1.5 text-xs text-gray-300">{item}</span>)}
              </div>
            </div>

            <div className="rounded-3xl border border-white/10 bg-[#15171c] p-6">
              <div className="flex items-center gap-3">
                <FileText className="h-5 w-5 text-violet-400" />
                <h3 className="font-medium text-white">解析内容</h3>
              </div>
              <div className="mt-5 grid grid-cols-2 gap-3">
                {[
                  ['场景切换', 'Scene'], ['人物台词', 'Dialogue'], ['动作行为', 'Action'], ['情绪节奏', 'Emotion'],
                ].map(([cn, en]) => (
                  <div key={cn} className="rounded-xl bg-white/[0.035] p-3">
                    <p className="text-sm text-gray-300">{cn}</p><p className="mt-1 text-[10px] uppercase tracking-wider text-gray-600">{en}</p>
                  </div>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </div>

      {isParsing && (
        <GenerationOverlay
          title="正在解析英文剧本"
          subtitle="大模型正在识别场景、角色关系、动作与情绪，并拆分为有序镜头。"
          duration={demoDuration(demoMode ? 10000 : 1800)}
          steps={['读取剧本结构与场景', '识别人物、动作和台词', '匹配专业镜头语言', '整理有序分镜列表']}
          onComplete={finishParsing}
        />
      )}
    </div>
  );
}

export default VideoPage;
